import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { morph } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by updating the media gallery.
   * In quick-add: instant slide select from option media, then morph (avoids full replaceWith teardown).
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const isQuickAdd = Boolean(event.target.closest('quick-add-dialog'));

    // Instant image swap while section HTML is still loading
    if (isQuickAdd && event.target instanceof HTMLElement) {
      const mediaId =
        event.target.dataset.optionMediaId ||
        event.target.closest('[data-option-media-id]')?.getAttribute('data-option-media-id');
      if (mediaId) this.#selectMediaById(mediaId);
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, resource } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        if (isQuickAdd) {
          // Morph keeps slideshow custom elements alive — much faster than replaceWith
          morph(this, newMediaGallery);

          const featuredId = resource?.featured_media?.id ?? resource?.featured_image?.id;
          if (featuredId) this.#selectMediaById(String(featuredId));
          return;
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * @param {string} mediaId
   */
  #selectMediaById(mediaId) {
    const slideshow = this.slideshow;
    if (!slideshow || !mediaId) return;

    const { slides = [] } = slideshow.refs ?? {};
    const index = slides.findIndex(
      (slide) =>
        slide.getAttribute('slide-id') === mediaId || Boolean(slide.querySelector(`[data-media-id="${mediaId}"]`))
    );
    if (index < 0) return;

    const slideId = slides[index]?.getAttribute('slide-id');
    if (slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
    } else {
      slideshow.select(index, undefined, { animate: false });
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
